import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import ThemeToggle from '../components/ThemeToggle';
import { Search, ChevronDown, ChevronUp, Book, HelpCircle, Calendar, Users, Camera, Settings } from 'lucide-react';
import './Support.css';

const knowledgeBase = [
  {
    category: "Getting Started",
    Icon: Book,
    articles: [
      {
        title: "Creating Your First Event",
        content: `To create a new event:

1. Navigate to the Events page from the sidebar
2. Click the "Create Event" button (green button at the top)
3. Fill in the event details:
   - Event Name: Give your event a descriptive name
   - Start Time: When the event begins
   - End Time: When the event ends (photos cannot be uploaded after this time)
4. Click "Create Event" to save

Your event will appear in the events list and will be active for photo uploads between the start and end times.`
      },
      {
        title: "Understanding the Dashboard",
        content: `The Dashboard provides an overview of your account:

- My Events: Total number of events you've created
- Photos Uploaded: Total photos across all your events
- Active Events: Events currently accepting photo uploads
- Subscription Usage: Shows how many events you've used vs. your plan limit

Quick Actions:
- View Events: Navigate to your events list
- Settings: Manage your account settings`
      },
      {
        title: "Navigating the Platform",
        content: `The sidebar on the left provides access to all main features:

- Dashboard: Overview and statistics
- Events: View and manage all your events
- Settings: Account information and password management
- Support: Access this knowledge base

Click any menu item to navigate to that section. The active page is highlighted with a blue gradient.`
      }
    ]
  },
  {
    category: "Managing Events",
    Icon: Calendar,
    articles: [
      {
        title: "Editing Event Details",
        content: `To edit an existing event:

1. Go to the Events page
2. Click "View Photos" on the event you want to edit
3. Click the "Edit Event" button at the top
4. Update any of the following:
   - Event Name
   - Start Time
   - End Time
5. Click "Save Changes"

Note: Changing the end time affects when users can upload photos. Photos cannot be uploaded after the end time has passed.`
      },
      {
        title: "Viewing Event Photos",
        content: `To view photos for an event:

1. Navigate to the Events page
2. Click "View Photos" on the desired event
3. You'll see all uploaded photos in a grid layout

The event photos page shows:
- Event name and total photo count
- Filter tabs (All Photos, Taken By, Appears In)
- Individual photo cards with photographer information
- Action buttons for managing photos`
      },
      {
        title: "Generating Event QR Codes",
        content: `QR codes allow photographers to quickly access your event:

1. Open the event's photos page
2. Click "Event QR" button
3. The QR code will be displayed
4. Share this QR code with photographers

When photographers scan the code, they'll be directed to upload photos to your event. This is the fastest way to onboard photographers at the event venue.`
      },
      {
        title: "Starting a Slideshow",
        content: `Display event photos in a fullscreen slideshow:

1. Navigate to the event's photos page
2. Click "Start Slideshow" button
3. The slideshow will begin in fullscreen mode
4. Photos advance automatically every few seconds
5. Press ESC or click the close button to exit

Perfect for displaying photos on a screen during or after the event!`
      }
    ]
  },
  {
    category: "Photo Management",
    Icon: Camera,
    articles: [
      {
        title: "Filtering Photos",
        content: `Use filter tabs to organize and find photos:

All Photos: Shows every photo uploaded to the event

Taken By: Filter photos by photographer
- Click the "Taken By" tab
- Search for a specific photographer
- Select photographers to view only their photos

Appears In: Filter photos by tagged attendees
- Click the "Appears In" tab
- Search for attendees
- View photos where specific people are tagged

Filters help you quickly find specific photos in large event galleries.`
      },
      {
        title: "Selecting and Downloading Photos",
        content: `To download one or more photos:

Single Photo:
1. Hover over a photo card
2. Click the "Download" button in the overlay

Multiple Photos:
1. Click "Select All Photos" or check individual photo checkboxes
2. Selected photos will be highlighted
3. Click the "Download" button at the top
4. Photos will be packaged into a ZIP file

Downloaded photos maintain their original quality and resolution.`
      },
      {
        title: "Deleting Photos",
        content: `To remove unwanted photos:

Single Photo:
1. Hover over the photo card
2. Click the "Delete" button (trash icon)
3. Confirm the deletion

Multiple Photos:
1. Select photos using checkboxes
2. Click the "Delete" button at the top
3. Confirm bulk deletion

Warning: Deleted photos cannot be recovered. Make sure you have backups if needed before deleting.`
      },
      {
        title: "Understanding Photo Information",
        content: `Each photo card displays:

- Photographer Name: Who uploaded the photo
- Upload Timestamp: When the photo was added
- Tagged Users: Attendees identified in the photo (if any)
- Selection Checkbox: For bulk actions

Hover over a photo to see the overlay with download and delete options.`
      }
    ]
  },
  {
    category: "User Management",
    Icon: Users,
    articles: [
      {
        title: "Managing Event Photographers",
        content: `Control who can upload photos to your event:

1. Open the event's photos page
2. Click "Manage Users" button
3. View all photographers who have requested access

Approving Photographers:
- Review pending requests
- Click "Approve" to grant upload access
- Approved photographers can immediately start uploading

Blocking Photographers:
- Click "Block" to revoke access
- Blocked users cannot upload photos
- Use this for inappropriate uploads or spam

Photographers must be approved before they can upload photos to your event.`
      },
      {
        title: "Tagging Attendees in Photos",
        content: `Tag people who appear in photos:

1. Select one or more photos using checkboxes
2. Click the tag button or option
3. Search for attendees by name
4. Select the people who appear in the selected photos
5. Save tags

Tagged photos can be filtered using the "Appears In" tab, making it easy for attendees to find photos of themselves.`
      }
    ]
  },
  {
    category: "Account Settings",
    Icon: Settings,
    articles: [
      {
        title: "Viewing Account Information",
        content: `Your Settings page displays:

- Name: Your full name
- Email: Account email address
- Phone: Contact phone number (if provided)
- Role: Your account role (Admin)
- Subscription: Your current plan tier

To edit your account information, click the "Edit" button next to Account Information.`
      },
      {
        title: "Changing Your Password",
        content: `To update your password:

1. Navigate to Settings from the sidebar
2. Scroll to the "Change Password" section
3. Enter your current password
4. Enter your new password (minimum 8 characters)
5. Confirm your new password
6. Click "Change Password"

Password requirements:
- Minimum 8 characters
- New password must match confirmation
- Current password must be correct

You'll receive a confirmation message when the password is successfully changed.`
      },
      {
        title: "Managing Subscription",
        content: `View your subscription details on the Settings page:

- Current plan tier (Enterprise, Professional, etc.)
- Event limits for your plan
- Usage statistics

To upgrade or modify your subscription, contact support. Your subscription determines:
- Maximum number of events you can create
- Storage limits
- Feature access`
      }
    ]
  },
  {
    category: "Mobile App",
    Icon: Camera,
    articles: [
      {
        title: "How Photographers Upload Photos",
        content: `Photographers use the mobile app to upload photos:

1. Download the event photo app on their mobile device
2. Scan the event QR code or enter the event ID
3. Request photographer access
4. Wait for admin approval
5. Once approved, take photos or select from gallery
6. Upload photos to the event

Photos appear in the admin dashboard immediately after upload. Photographers can see their upload status in the app.`
      },
      {
        title: "Photographer Access Requests",
        content: `When a photographer requests access:

1. You'll see a pending request in "Manage Users"
2. Review the photographer's name and information
3. Approve or deny the request

Best practices:
- Approve known photographers immediately
- Review unfamiliar requests before approving
- Block users who upload inappropriate content
- Communicate QR codes only to authorized photographers`
      }
    ]
  },
  {
    category: "Professional Camera FTP Upload",
    Icon: Camera,
    articles: [
      {
        title: "Camera FTP Compatibility Guide",
        content: `EventPic Live supports direct FTP upload from professional cameras with built-in FTP capabilities. This allows photographers to automatically upload photos as they shoot, without needing a mobile app or computer.

**Compatible Camera Brands & Models:**

**Canon:**
✅ EOS R3 - Full FTP support (WiFi/Ethernet)
✅ EOS R5 - Full FTP support (WiFi/Ethernet)
✅ EOS R5 C - Full FTP support
✅ EOS R6 Mark II - Full FTP support (WiFi)
✅ EOS R6 - Full FTP support (WiFi)
✅ EOS-1D X Mark III - Full FTP support (Ethernet/WiFi)
✅ EOS-1D X Mark II - Full FTP support (Ethernet)
✅ EOS 5D Mark IV - FTP via WiFi adapter

**Sony:**
✅ Alpha 1 (A1) - Full FTP support (WiFi/Ethernet)
✅ Alpha 9 III (A9 III) - Full FTP support
✅ Alpha 9 II (A9 II) - Full FTP support
✅ Alpha 9 (A9) - Full FTP support
✅ Alpha 7R V (A7R V) - Full FTP support
✅ Alpha 7R IV (A7R IV) - Full FTP support
✅ Alpha 7S III (A7S III) - Full FTP support
✅ Alpha 7 IV (A7 IV) - Full FTP support

**Nikon:**
✅ Z9 - Full FTP support (WiFi/Ethernet)
✅ Z8 - Full FTP support (WiFi/Ethernet)
✅ D6 - Full FTP support (Ethernet/WiFi)
✅ D5 - Full FTP support (Ethernet/WiFi)
✅ D4S - Full FTP support (Ethernet)
✅ D4 - Full FTP support (Ethernet)

**Fujifilm:**
✅ X-H2S - FTP support via CamRanger
✅ X-T5 - FTP support via CamRanger
✅ GFX100 II - FTP support via CamRanger

**Requirements:**
- Camera must have built-in WiFi or Ethernet port
- WiFi network connection (mobile hotspot works)
- FTP credentials from EventPic Live camera registration

**Note:** Models not listed may still support FTP through third-party accessories like CamRanger 2 or wireless transmitters. Check your camera's manual for FTP transfer capabilities.`
      },
      {
        title: "Setting Up Camera FTP Upload",
        content: `To configure your professional camera for direct FTP upload:

**Step 1: Register Your Camera**
1. Open the EventPic Live dashboard
2. Navigate to your event
3. Click "Camera Setup" or scan the Camera QR code
4. Fill in your details (name, email, phone, camera model)
5. Save your FTP credentials (server, username, password)

**Step 2: Configure Camera FTP Settings**

For Canon (R3, R5, R6):
1. Menu → Communication Settings → FTP Transfer
2. Enable FTP Transfer
3. Select "Set FTP Server"
4. Enter server address: 18.116.21.55
5. Port: 21
6. Enter your FTP username and password
7. Set upload path: /uploads
8. Enable "Auto Send" for automatic upload

For Sony (A1, A9, A7 series):
1. Menu → Network → FTP Transfer Function
2. FTP Server Settings → Enter server details
3. Server: 18.116.21.55, Port: 21
4. Login Name: Your FTP username
5. Password: Your FTP password
6. Destination Folder: /uploads
7. Enable "Auto Transfer"

For Nikon (Z9, Z8, D6):
1. Menu → Network → FTP Upload
2. Create/Edit FTP Connection
3. Server Address: 18.116.21.55
4. Port: 21
5. Username and Password from registration
6. Destination Folder: /uploads
7. Enable "Auto Send"

**Step 3: Connect to WiFi**
- Connect camera to WiFi network or mobile hotspot
- Test connection with a test photo
- Photos will appear in your event gallery within seconds

**Troubleshooting:**
- Ensure WiFi signal is strong
- Verify FTP credentials are entered correctly
- Check that event is still active (not past end time)
- Restart camera if connection fails`
      },
      {
        title: "Camera FTP vs Mobile App",
        content: `**When to Use Camera FTP:**
✅ Professional event photography
✅ High-volume shooting (weddings, sports, conferences)
✅ Instant photo delivery to clients
✅ Multiple photographers with pro cameras
✅ Automatic upload without manual intervention

**When to Use Mobile App:**
✅ Casual event photography
✅ Smartphone photographers
✅ Guest photo contributions
✅ Quick uploads from phone gallery
✅ No professional camera equipment

**Key Differences:**

Camera FTP:
- Automatic upload as you shoot
- No phone needed
- Full resolution RAW/JPEG
- Requires compatible pro camera
- Direct from camera to cloud

Mobile App:
- Manual photo selection
- Works with any smartphone
- JPEG only
- No special equipment needed
- Upload from phone gallery

**Best Practice:**
Use both! Professional photographers can use FTP for main coverage, while guests use the mobile app for candid moments. All photos appear in the same event gallery.`
      }
    ]
  },
  {
    category: "Tips & Best Practices",
    Icon: HelpCircle,
    articles: [
      {
        title: "Organizing Large Events",
        content: `For events with many photos:

Before the Event:
- Set accurate start and end times
- Generate QR code in advance
- Brief photographers on upload process

During the Event:
- Display QR code prominently
- Approve photographer requests quickly
- Monitor photo uploads in real-time
- Use slideshow mode for live display

After the Event:
- Review all uploaded photos
- Delete any inappropriate or duplicate photos
- Tag attendees for easy searching
- Download photos for backup
- Share gallery links with attendees`
      },
      {
        title: "Maximizing Photo Quality",
        content: `Ensure the best photo experience:

For Admins:
- Encourage photographers to upload high-resolution photos
- Review photos regularly and remove low-quality uploads
- Provide clear guidelines to photographers

Photo Display:
- Thumbnails are optimized for fast loading
- Downloads provide full-resolution originals
- Photos maintain original quality in storage

Storage:
- Downloaded photos are full resolution
- Cloud storage ensures photos are never lost
- Regular backups recommended for important events`
      },
      {
        title: "Event Security & Privacy",
        content: `Protect your event photos:

Access Control:
- Only approved photographers can upload
- Block suspicious or inappropriate users immediately
- Review photographer requests before approval

Photo Moderation:
- Monitor uploads for inappropriate content
- Delete problematic photos promptly
- Block users who violate guidelines

Data Privacy:
- Photos are stored securely in the cloud
- Only event admins can delete photos
- Attendee information is protected

Always review your event's photos periodically to ensure quality and appropriateness.`
      },
      {
        title: "Troubleshooting Common Issues",
        content: `Solutions to common problems:

Photos Not Appearing:
- Refresh the page
- Check if event end time has passed
- Verify photographer was approved

Cannot Delete Photos:
- Ensure you're logged in as admin
- Check internet connection
- Try refreshing the page

QR Code Not Working:
- Regenerate the QR code
- Ensure event is active (between start and end times)
- Verify photographers have the mobile app installed

Slow Photo Loading:
- Large galleries may take time to load
- Thumbnails load first for better performance
- Check your internet connection

If issues persist, contact support for assistance.`
      }
    ]
  },
  {
    category: "AI & Face Recognition",
    Icon: Users,
    articles: [
      {
        title: "How Face Recognition Works",
        content: `Our AI-powered face recognition system automatically identifies attendees in photos:

Registration Process:
1. Attendees download the mobile app
2. Scan the event QR code
3. Take a quick selfie to create their face profile
4. Enter their phone number for photo delivery

Photo Matching:
- When photos are uploaded, our AI detects all faces
- Each face is matched against registered attendee profiles
- Matched photos are automatically sent via text message
- Processing typically takes 2-5 minutes per photo

Accuracy:
- Can detect up to 20 faces per photo
- Works best with well-lit, clear photos
- Minimum recommended resolution: 1920x1080`
      },
      {
        title: "Troubleshooting Face Recognition",
        content: `Common issues and solutions:

Poor Selfie Quality:
- Issue: Too dark, face partially covered, sunglasses, extreme angles
- Solution: Have attendees retake selfie in good lighting with face clearly visible

Multiple People in Photos:
- AI can detect up to 20 faces per photo
- Very crowded photos (50+ people) may have reduced accuracy
- Processing may take longer for group photos

Changed Appearance:
- If attendees change appearance during event (glasses, hats, masks), accuracy may decrease
- Attendees can update their selfie in the app

Low-Quality Event Photos:
- Blurry, dark, or low-resolution photos reduce accuracy
- Ensure photographers use good lighting and focus
- Minimum recommended resolution: 1920x1080

Manual Assignment:
- If photo isn't automatically matched, attendees can browse all photos
- Organizers can manually assign photos in the dashboard`
      },
      {
        title: "Photo Delivery via SMS",
        content: `Automatic photo delivery system:

How It Works:
1. Photos are uploaded to the event
2. AI identifies faces and matches to attendees
3. Photos are sent via MMS text message within seconds
4. Attendees receive their actual photos directly (not links)

Gallery Links:
- Gallery links are NOT sent automatically
- Admin can choose to send a "Thank You" blast with gallery link
- Admin can manually send gallery links to specific attendees
- Links are optional - photos are delivered via MMS by default

Delivery Issues:

Photos Not Received:
- Check phone number was entered correctly
- Verify phone can receive MMS messages
- Check if blocked as spam
- Some carriers may block MMS from unknown numbers

Delayed Delivery:
- Normal: Within seconds of face match
- Large group photos may take slightly longer to process
- Check dashboard for processing status

International Numbers:
- Supports MMS delivery to 200+ countries
- Some carriers may have delays or not support MMS
- Contact your carrier if MMS not working

Carrier Blocking:
- Some carriers block automated MMS messages
- Check spam/blocked messages
- Add our number to contacts
- Admin can manually resend if needed

Resend Photos:
- Go to Event → Attendees → Select User → Resend Photos`
      }
    ]
  },
  {
    category: "Advanced Features",
    Icon: Settings,
    articles: [
      {
        title: "API Integration (Enterprise)",
        content: `Integrate EventPic Live with your existing systems:

API Access:
- Enterprise plans include full API access
- Generate API keys from Settings → API Keys
- Keep keys secure, never share publicly

Automated Photo Upload:
- Upload photos from camera systems or photo booths
- Supports batch uploads
- Webhook notifications available

Event Management:
- Create and manage events programmatically
- Useful for event management platforms
- Full CRUD operations supported

Attendee Data Export:
- Export attendee lists and photo delivery stats
- Engagement metrics available
- JSON and CSV formats supported

Webhook Notifications:
- Real-time notifications for:
  * New attendee registration
  * Photo uploaded
  * Photo delivered
  * Event ended`
      },
      {
        title: "Custom Branding (Enterprise)",
        content: `White-label options for Enterprise customers:

Custom Domain:
- Use your own domain for photo galleries
- Example: photos.yourevent.com
- Configure DNS settings and SSL through dashboard

Branded Mobile App:
- White-labeled app with your logo and colors
- Custom branding throughout
- Contact support for pricing

Custom Email Templates:
- Customize emails sent to attendees
- Add your branding, logo, and messaging
- Edit in Settings → Branding

Remove EventPic Branding:
- Remove all EventPic Live branding
- Applies to galleries, emails, and text messages
- Enable in Settings → Branding → White Label Mode`
      },
      {
        title: "Analytics & Reporting",
        content: `Track event performance and engagement:

Real-Time Dashboard:
- Monitor live stats during your event:
  * Attendees registered
  * Photos uploaded
  * Photos delivered
  * Average delivery time
  * Engagement rate

Post-Event Reports:
- Generate detailed reports after the event:
  * Total reach
  * Photos per attendee
  * Delivery success rate
  * Peak upload times
  * Attendee demographics

Export Data:
- Export to CSV or Excel:
  * Attendee list with contact info
  * Photo delivery logs
  * Engagement metrics
  * Revenue attribution

Engagement Metrics:
- Track:
  * Gallery views
  * Photo downloads
  * Social shares
  * Text message open rates
  * App usage patterns`
      },
      {
        title: "Multiple Photographers",
        content: `Set up team members or photographers:

Inviting Photographers:
- Invite by email from the dashboard
- Each gets their own login credentials
- Track who uploaded which photos

Photographer Permissions:
- Control what each photographer can access
- Limit to specific events if needed
- View upload history per photographer

Monitoring Uploads:
- See real-time upload activity
- Track photos by photographer
- Review and approve before delivery

Best Practices:
- Brief photographers on upload process
- Provide clear quality guidelines
- Monitor uploads during the event
- Review all photos before delivery`
      }
    ]
  }
];

function Support() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [expandedArticles, setExpandedArticles] = useState({});
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });

  const toggleCategory = (categoryName) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryName]: !prev[categoryName]
    }));
  };

  const toggleArticle = (categoryName, articleTitle) => {
    const key = `${categoryName}-${articleTitle}`;
    setExpandedArticles(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Listen for sidebar collapse changes
  useEffect(() => {
    const handleStorageChange = () => {
      setSidebarCollapsed(localStorage.getItem('sidebarCollapsed') === 'true');
    };
    window.addEventListener('storage', handleStorageChange);
    const interval = setInterval(handleStorageChange, 100);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Filter articles based on search query
  const filteredKnowledgeBase = knowledgeBase.map(category => ({
    ...category,
    articles: category.articles.filter(article =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
      category.category.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.articles.length > 0);

  // Check if user is super admin
  const getUserData = () => {
    const stored = localStorage.getItem('user');
    if (!stored || stored === 'undefined') return null;
    try {
      return JSON.parse(stored);
    } catch (e) {
      return null;
    }
  };
  const userData = getUserData();
  const isSuperAdmin = userData?.role === 'super_admin';

  return (
    <div className={`page-container ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <ThemeToggle />
      <Sidebar onLogout={() => { localStorage.clear(); navigate('/'); }} isSuperAdmin={isSuperAdmin} />
      
      <div className="support-content">
        <div className="support-header">
          <div className="support-header-content">
            <div className="support-title-section">
              <Book size={40} className="support-icon" />
              <div>
                <h1>Support & Knowledge Base</h1>
                <p>Everything you need to know about using the platform</p>
              </div>
            </div>
          </div>
        </div>

        <div className="support-main">
          {/* Search Bar */}
          <div className="search-section">
            <div className="search-container">
              <Search className="search-icon" size={20} />
              <input
                type="text"
                placeholder="Search for help articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          {/* Knowledge Base */}
          <div className="knowledge-base">
            {filteredKnowledgeBase.length === 0 ? (
              <div className="no-results">
                <HelpCircle size={48} />
                <h3>No articles found</h3>
                <p>Try searching with different keywords</p>
              </div>
            ) : (
              filteredKnowledgeBase.map((category) => (
                <div key={category.category} className="category-section">
                  <button
                    className="category-header"
                    onClick={() => toggleCategory(category.category)}
                  >
                    <div className="category-title">
                      <span className="category-icon"><category.Icon size={24} /></span>
                      <h2>{category.category}</h2>
                      <span className="article-count">({category.articles.length} articles)</span>
                    </div>
                    {expandedCategories[category.category] ? (
                      <ChevronUp size={24} />
                    ) : (
                      <ChevronDown size={24} />
                    )}
                  </button>

                  {expandedCategories[category.category] && (
                    <div className="articles-list">
                      {category.articles.map((article) => {
                        const articleKey = `${category.category}-${article.title}`;
                        const isExpanded = expandedArticles[articleKey];

                        return (
                          <div key={article.title} className="article-card">
                            <button
                              className="article-header"
                              onClick={() => toggleArticle(category.category, article.title)}
                            >
                              <h3>{article.title}</h3>
                              {isExpanded ? (
                                <ChevronUp size={20} />
                              ) : (
                                <ChevronDown size={20} />
                              )}
                            </button>

                            {isExpanded && (
                              <div className="article-content">
                                {article.content.split('\n').map((paragraph, idx) => {
                                  // Check if line ends with colon (section header)
                                  const isHeader = paragraph.trim().endsWith(':') && !paragraph.trim().startsWith('-') && !paragraph.trim().match(/^\d+\./);
                                  return (
                                    <p key={idx} style={isHeader ? {fontWeight: '600', marginTop: idx > 0 ? '12px' : '0'} : {}}>
                                      {paragraph}
                                    </p>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      
    </div>
  );
}

export default Support;
